import { jwtDecode } from 'jwt-decode';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { $$api } from '@doger/core/shared/api';

import { useAuthContext } from '../auth.context';
import { JWTDomain } from './jwt.domain';
import { JWTTokenSchema } from './jwt.schema';
import { useJWTTokens } from './use-jwt-tokens.hook';

export type JWTGuardProps = {
  children: ReactNode;
  fallback?: ReactNode;
};

export const JWTGuard = ({ children, fallback }: JWTGuardProps) => {
  const tokens = useJWTTokens();
  const { signInUrl } = useAuthContext();
  const location = useLocation();

  if (!tokens.accessToken || !tokens.refreshToken) {
    setTimeout(() => {
      $$api.tokensService.removeTokens();
    });

    return fallback ?? <Navigate to={signInUrl} state={{ from: location }} />;
  }

  const result = safeDecodeJWT(tokens.refreshToken);

  if (!result.success) {
    setTimeout(() => {
      $$api.tokensService.removeTokens();
    });

    return fallback ?? <Navigate to={signInUrl} state={{ from: location }} />;
  }

  const refreshTokenPayload = JWTTokenSchema.safeParse(result.payload);

  if (!refreshTokenPayload.success) {
    setTimeout(() => {
      $$api.tokensService.removeTokens();
    });

    return fallback ?? <Navigate to={signInUrl} state={{ from: location }} />;
  }

  if (JWTDomain.isTokenPayloadExpired(refreshTokenPayload.data)) {
    setTimeout(() => {
      $$api.tokensService.removeTokens();
    });

    return fallback ?? <Navigate to={signInUrl} state={{ from: location }} />;
  }

  // Tokens are valid, app should take care of refreshing them, if accessToken is expired

  return children;
};

function safeDecodeJWT(token: string) {
  try {
    const payload = jwtDecode(token) as Record<string, unknown>;

    return { success: true, payload };
  } catch (error) {
    return { success: false, error };
  }
}
